import { type MouseEvent, useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import { getIsEuTenant, getIsLatamTenant } from '../../../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { trace } from '../../../../../configuration/setup/trace';
import { useGetAssetsQuery } from '../../../../services/assetAdministrationApi';
import {
    getEasyOnboardingSelectedAssetId,
    // enableCloseDialogTriggersReload,
    clearConnectorDialogTitleOverwrite,
    getShowEasyOnboardingDialog,
} from '../../../reducers/assets/assetsSlice';
import { AssetType } from '../types';
import { DialogFooter } from './DialogFooter';
import { FormStepConnector } from './FormStepConnector';
import { FormStepSelectAssetType } from './FormStepSelectAssetType';
import { FormStepSelectBrand } from './FormStepSelectBrand';
import { FormStepSelectTelematics } from './FormStepSelectTelematics';
import { SimpleDialogWrapper } from './SimpleDialogWrapper';
import { ASSET_TYPE_TILES } from './configuration/assetTypeTileConfig';
import { ALL_BRAND_TILES } from './configuration/brandTileConfig';
import { TELEMATICS_TILES } from './configuration/telematicsTileConfig';
import type { AssetTypeTile, BrandTile, TelematicsTile } from './configuration/types';
import { BrandSearchParamType, StepSearchParamType, VehicleTypeSearchParamType } from './types';

export const FormStep = {
    connector: 'connector',
    selectAssetType: 'selectAssetType',
    selectBrand: 'selectBrand',
    selectTelematics: 'selectTelematics',
} as const;

export type FormStep = (typeof FormStep)[keyof typeof FormStep];

export const FormStepNavigation = {
    prev: 'prev',
    next: 'next',
};

export type FormStepNavigation = (typeof FormStepNavigation)[keyof typeof FormStepNavigation];

const initialState = {
    formStep: FormStep.selectAssetType,
    selectedAssetType: undefined,
};
const EasyOnboardingDialog = () => {
    const [formStep, setFormStep] = useState<FormStep>(initialState.formStep);
    const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeTile | undefined>(
        initialState.selectedAssetType
    );
    const [selectedBrand, setSelectedBrand] = useState<BrandTile | undefined>(undefined);
    const [selectedTelematic, setSelectedTelematic] = useState<TelematicsTile | undefined>(undefined);

    const selectedAssetId = useAppSelector(getEasyOnboardingSelectedAssetId);
    const { data: assets = [] } = useGetAssetsQuery();
    const isEuTenant = useAppSelector(getIsEuTenant);
    const isLatamTenant = useAppSelector(getIsLatamTenant);
    const showDialog = useAppSelector(getShowEasyOnboardingDialog);

    const location = useLocation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(location.search);
        const assetType = urlSearchParams.get(VehicleTypeSearchParamType);
        const brand = urlSearchParams.get(BrandSearchParamType);
        const step = urlSearchParams.get(StepSearchParamType);

        if (assetType && assetType in AssetType) {
            setSelectedAssetType(ASSET_TYPE_TILES[assetType as AssetType]);
        }

        if (brand) {
            const selectedBrand = ALL_BRAND_TILES.find(brandTile => brandTile.name === brand);
            setSelectedBrand(selectedBrand);
        }

        if (step && step in FormStep) {
            setFormStep(step as FormStep);
        }
    }, [location]);

    useEffect(() => {
        if (!showDialog && formStep !== initialState.formStep) {
            setFormStep(initialState.formStep);
            setSelectedBrand(undefined);
            setSelectedTelematic(undefined);
        }
    }, [showDialog, formStep]);

    useEffect(() => {
        if (selectedAssetId !== undefined) {
            const selectedAsset = assets.find(it => it.id === selectedAssetId);
            const alreadySelectedBrand = ALL_BRAND_TILES.find(
                it => it.displayName.toLowerCase().trim() === selectedAsset?.brand?.toLowerCase()
            );
            trace('selectedAsset\n', selectedAsset);
            if (alreadySelectedBrand) {
                setSelectedBrand(alreadySelectedBrand);
            }
            if (selectedAsset?.type !== undefined) {
                setSelectedAssetType(ASSET_TYPE_TILES[selectedAsset?.type]);
            }
            if (isEuTenant) {
                setFormStep(FormStep.selectTelematics);
            } else {
                // non-eu tenants should not see the brand/telematics selection
                // when adding a telematics device from sidebar
                const alreadySelectedAssetType = selectedAsset?.type
                    ? ASSET_TYPE_TILES[selectedAsset?.type]
                    : undefined;
                setSelectedAssetType(alreadySelectedAssetType);
                if (selectedAsset && selectedAsset.type !== 'trailer') {
                    setFormStep(FormStep.selectTelematics);
                } else {
                    setSelectedTelematic(TELEMATICS_TILES.noTelematics);
                    // dispatch(enableCloseDialogTriggersReload());
                    setFormStep(FormStep.connector);
                }
            }
        }
    }, [assets, selectedAssetId, isEuTenant]);

    useEffect(() => {
        trace(
            '\nformStep',
            formStep,
            '\nselectedAssetType',
            selectedAssetType,
            '\nselectedBrand',
            selectedBrand,
            '\nselectedTelematicsBrand',
            selectedTelematic
        );
    });

    const handleFormStepNavigation = (goto: FormStepNavigation, e?: MouseEvent) => {
        if (goto === FormStepNavigation.prev) {
            if (formStep === FormStep.selectBrand) {
                setFormStep(FormStep.selectAssetType);
                setSelectedAssetType(undefined);
            } else if (formStep === FormStep.selectTelematics) {
                if (isEuTenant) {
                    setFormStep(FormStep.selectBrand);
                    if (selectedBrand !== undefined) {
                        setSelectedBrand(undefined);
                        setSelectedTelematic(undefined);
                    }
                } else {
                    // for tenant != rio.eu jump back to asset type selection
                    setFormStep(FormStep.selectAssetType);
                    setSelectedTelematic(undefined);
                    setSelectedAssetType(undefined);
                }
            }
        } else if (goto === FormStepNavigation.next) {
            if (isEuTenant) {
                switch (formStep) {
                    case FormStep.selectAssetType:
                        setFormStep(FormStep.selectBrand);
                        break;
                    case FormStep.selectBrand:
                        setFormStep(FormStep.selectTelematics);
                        break;
                    case FormStep.selectTelematics:
                        // dispatch(enableCloseDialogTriggersReload());
                        // reset before going to connector to start with a clean state
                        dispatch(clearConnectorDialogTitleOverwrite());
                        setFormStep(FormStep.connector);
                        break;
                    case FormStep.connector:
                        break;
                }
            } else {
                // for tenant != rio.eu jump directly to connector dialog
                switch (formStep) {
                    case FormStep.selectAssetType:
                        // for non-eu tenants go directly to connector screen
                        if ((e ? (e.target as HTMLImageElement) : { alt: 'unknown' }).alt === 'trailer') {
                            // dispatch(enableCloseDialogTriggersReload());
                            setFormStep(FormStep.connector);
                        } else {
                            setFormStep(FormStep.selectTelematics);
                        }
                        break;
                    case FormStep.selectTelematics:
                        // dispatch(enableCloseDialogTriggersReload());
                        setFormStep(FormStep.connector);
                        break;
                    case FormStep.connector:
                        break;
                }
            }
        }
    };

    const renderDialogBody = () => {
        switch (formStep) {
            case FormStep.selectBrand:
                return (
                    <FormStepSelectBrand
                        selectedAssetType={selectedAssetType}
                        setSelectedBrand={setSelectedBrand}
                        handleFormStepNavigation={handleFormStepNavigation}
                    />
                );
            case FormStep.selectTelematics:
                return (
                    <FormStepSelectTelematics
                        selectedAssetId={selectedAssetId}
                        selectedBrand={selectedBrand}
                        selectedTelematic={selectedTelematic}
                        selectedAssetType={selectedAssetType}
                        setSelectedTelematic={setSelectedTelematic}
                    />
                );
            case FormStep.connector:
                return (
                    <FormStepConnector
                        selectedTelematic={selectedTelematic}
                        selectedAssetType={selectedAssetType}
                        selectedBrand={selectedBrand}
                    />
                );
            // case FormStep.selectAssetType:
            default:
                return (
                    <FormStepSelectAssetType
                        setSelectedAssetType={setSelectedAssetType}
                        handleFormStepNavigation={handleFormStepNavigation}
                        isLatamTenant={isLatamTenant}
                    />
                );
        }
    };
    const dialogContent = (
        <div>
            {renderDialogBody()}
            <DialogFooter
                formStep={formStep}
                handleFormStepNavigation={handleFormStepNavigation}
                setSelectedTelematics={setSelectedTelematic}
                selectedTelematics={selectedTelematic}
                selectedAssetType={selectedAssetType}
                setSelectedBrand={setSelectedBrand}
                selectedBrand={selectedBrand}
                selectedAssetId={selectedAssetId}
            />
        </div>
    );
    return (
        <SimpleDialogWrapper
            formStep={formStep}
            selectedAssetType={selectedAssetType}
            selectedBrand={selectedBrand}
            selectedTelematics={selectedTelematic}
            dialogContent={dialogContent}
        />
    );
};

export default EasyOnboardingDialog;
